/**
 * Centralized step-up TOTP interceptor (docs/305 §4.3, §6.4).
 *
 * When the API answers a high-risk operation with 428 STEP_UP_REQUIRED, the
 * apiRequest client calls `promptForGaCode()`, which surfaces a TOTP modal (the
 * UI handler is registered by <GaProvider>) and resolves with the entered code.
 * apiRequest then transparently retries the original request with the code in
 * the `x-ga-code` header. High-risk ops never wire this up themselves.
 */

type GaPromptFn = () => Promise<string>;

let promptFn: GaPromptFn | null = null;

/** Registered by <GaProvider> on mount. */
export function registerGaPrompt(fn: GaPromptFn): void {
  promptFn = fn;
}

/** Cleared by <GaProvider> on unmount. */
export function unregisterGaPrompt(): void {
  promptFn = null;
}

/** Resolve with a 6-digit TOTP code; rejects if no handler or the user cancels. */
export function promptForGaCode(): Promise<string> {
  if (!promptFn) throw new Error("Step-up TOTP prompt is not initialized");
  return promptFn();
}

/** Sentinel thrown when the user cancels the step-up modal. */
export class GaCancelledError extends Error {
  constructor() {
    super("Step-up verification cancelled");
    this.name = "GaCancelledError";
  }
}
